"use client";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const ProductsContext = createContext();

export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  const PRODUCTS_ENDPOINT = `${BASE_URL}/products`;
  const { token, logout } = useAuth();

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  });

  const handleAuthError = (res) => {
    if (res.status === 401) {
      logout();
      throw new Error("Unauthorized");
    }
  };

  const safeParseArray = (value) => {
    try {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      return JSON.parse(value);
    } catch {
      return [];
    }
  };

  const safeParseJSON = (value) => {
    try {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      return JSON.parse(value);
    } catch {
      return [];
    }
  };

  /**
   * Resolve an image path to a full URL.
   * Handles both CDN-relative paths and absolute URLs.
   */
  const resolveUrl = (path) => {
    if (!path) return "";
    return path.startsWith("http") ? path : `${CDN_URL}/${path}`;
  };

  /**
   * Normalize a raw API product object into the shape the UI expects.
   *
   * Product Details API shape (returned by server):
   *   product.descriptions = [
   *     { title: "...", content: "...", image: "path/or/url" },
   *     …
   *   ]
   *
   * Normalized shape consumed by AddProductPage / edit mode:
   *   productDetails = [
   *     { title: "...", content: "...", image: null, preview: "full-url" },
   *     …
   *   ]
   */
  const normalizeProduct = (p = {}) => {
    // ─── Prices / counts (coerce to Number) ────────────────────────────────
    const num = (v) => Number(String(v ?? 0).trim()) || 0;

    // ─── Product Details ────────────────────────────────────────────────────
    // The API may return them under `descriptions` or `product_details` or `description`.
    // We fall back gracefully whichever key is present.
    let rawDetails = [];

    if (Array.isArray(p.descriptions)) {
      rawDetails = p.descriptions;
    } else if (Array.isArray(p.product_details)) {
      rawDetails = p.product_details;
    } else if (p.description) {
      // Parse the description field as it contains JSON data
      rawDetails = safeParseJSON(p.description);
    }

    const productDetails = rawDetails.map((d) => ({
      title:   d.title   || "",
      content: d.content || "",
      image:   d.image || null,  // Preserve the image path if it exists
      preview: d.image ? resolveUrl(d.image) : null,
    }));

    return {
      id: p.id,
      name:              p.product_name        || "",
      brand_name:        p.brand_name          || "",
      description:       p.description         || "",
      manufacturer:      p.manufacturer        || "",
      item_type_name:    p.item_type_name       || "",
      frame_color:       p.frame_color         || "",
      item_shape:        p.item_shape          || "",
      color:             p.color               || "",
      material:          p.material            || "",
      model_name:        p.model_name          || "",
      frame_type:        p.frame_type          || "",
      frame_material:    p.frame_material      || "",
      size:              p.size                || "",
      style:             p.style               || "",
      product_type:      p.product_type        || "",
      return_policy:     p.return_policy  ? String(p.return_policy)  : '',
      product_policy:    p.product_policy || '',

      // ─── Pricing
      your_price:                num(p.your_price),
      max_retail_price:          num(p.max_retail_price),
      min_seller_allowed_price:  num(p.min_seller_allowed_price),
      max_seller_allowed_price:  num(p.max_seller_allowed_price),
      sale_price:                num(p.sale_price),
      price:                     num(p.sale_price),
      originalPrice:             num(p.offer_price),

      // ─── Stock and Order Quantities
      stock:         num(p.quantity),
      quantity:      num(p.quantity),
      min_order_qty: num(p.min_order_qty),  // Fix: properly map min_order_qty

      // ─── Category hierarchy
      categoryId:         String(p.category_id       || ""),
      category_name:      String(p.category_name     || ""),
      subCategoryId:      String(p.subcategory_id    || ""),
      subcategory_name:   String(p.subcategory_name  || ""),
      modalCategoryId:    p.model_category_id ? String(p.model_category_id) : "",

      // ─── Identifiers
      sku:    p.sku    || "",
      status: p.status === "Active" ? "Active" : "Inactive",
      admin_status: p.admin_status || "",

      // ─── Units & counts
      unit_count:      p.unit_count      || "",
      unit_count_type: p.unit_count_type || "",

      // ─── Product dimensions
      item_weight:              p.item_weight              || "",
      item_weight_unit:         p.item_weight_unit         || "kg",
      item_length_longer_edge:  p.item_length_longer_edge  || "",
      item_length_unit:         p.item_length_unit         || "cm",
      item_width_shorter_edge:  p.item_width_shorter_edge  || "",
      item_width_unit:          p.item_width_unit          || "cm",
      item_depth:               p.item_depth               || "",

      // ─── Package dimensions
      item_package_length:  p.item_package_length  || "",
      package_length_unit:  p.package_length_unit  || "cm",
      item_package_width:   p.item_package_width   || "",
      package_width_unit:   p.package_width_unit   || "cm",
      item_package_height:  p.item_package_height  || "",
      package_height_unit:  p.package_height_unit  || "cm",
      item_package_weight:  p.item_package_weight  || "",
      package_weight_unit:  p.package_weight_unit  || "kg",

      // ─── Pricing extras
      prod_tax_code:        p.prod_tax_code        || "",
      sale_start_date:      p.sale_start_date      || "",
      sale_end_date:        p.sale_end_date        || "",
      offering_relase_date: p.offering_relase_date || "",
      merchant_release_date:p.merchant_release_date|| "",
      max_order_quantity:   p.max_order_quantity   || "",
      offering_message:     p.offering_message     || "",
      wrap_available:       p.wrap_available === "1" || p.wrap_available === 1 || p.wrap_available === true,

      // ─── Media
      images: safeParseArray(p.product_images).map(resolveUrl),
      video:  p.video ? resolveUrl(p.video) : "",
      tags:   safeParseArray(p.tags),

      // ─── Product Details (mapped from API `descriptions` array or parsed description)
      productDetails,
    };
  };

  // ─── Fetch products ──────────────────────────────────────────────────────────
  const refreshProducts = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(PRODUCTS_ENDPOINT, { headers: getHeaders() });
      handleAuthError(res);

      const json = await res.json();
      const normalized = Array.isArray(json?.data)
        ? json.data.map(normalizeProduct)
        : [];

      setProducts(normalized);
    } catch (err) {
      setError("Failed to load products");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // ─── Add product ─────────────────────────────────────────────────────────────
  const addProduct = async (formData) => {
    try {
      const res = await fetch(PRODUCTS_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          // ❌ Do NOT set Content-Type when sending FormData
        },
        body: formData,
      });

      const contentType = res.headers.get("content-type");
      let json = null;
      if (contentType?.includes("application/json")) json = await res.json();

      if (res.status === 401)
        return { success: false, status: 401, message: "Unauthorized. Please login again." };

      if (res.status === 422)
        return { success: false, status: 422, message: "Validation failed", errors: json?.errors || {} };

      if (res.status === 201 || res.status === 200) {
        await refreshProducts();
        return { success: true, status: res.status, data: json?.data };
      }

      return { success: false, status: res.status, message: json?.message || "Operation failed" };

    } catch (err) {
      return { success: false, message: "Network error. Please try again." };
    }
  };

  // ─── Update product ──────────────────────────────────────────────────────────
  const updateProduct = async (id, payload) => {
    try {
      const isFormData = payload instanceof FormData;

      const res = await fetch(`${PRODUCTS_ENDPOINT}/${id}`, {
        method: "POST",
        headers: isFormData
          ? { Authorization: `Bearer ${token}`, Accept: "application/json" }
          : getHeaders(),
        body: isFormData ? payload : JSON.stringify(payload),
      });

      handleAuthError(res);

      const contentType = res.headers.get("content-type");
      let json = null;
      if (contentType?.includes("application/json")) json = await res.json();

      if (res.status === 401)
        return { success: false, status: 401, message: "Unauthorized. Please login again." };

      if (res.status === 422)
        return { success: false, status: 422, message: "Validation failed", errors: json?.errors || {} };

      if (![200, 201].includes(res.status))
        return { success: false, status: res.status, message: json?.message || `HTTP Error ${res.status}` };

      const updatedProduct = normalizeProduct(json?.data);
      setProducts(prev =>
        prev.map(p => String(p.id) === String(id) ? updatedProduct : p)
      );

      return { success: true, status: res.status, data: updatedProduct };

    } catch (err) {
      return { success: false, message: "Network error while updating product" };
    }
  };

  // ─── Toggle status ───────────────────────────────────────────────────────────
  const toggleProductStatus = async (id) => {
    const product = products.find(p => p.id === id);
    if (!product) return;

    const newStatus = product.status === "Active" ? "Inactive" : "Active";

    try {
      const res = await fetch(`${PRODUCTS_ENDPOINT}/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, Accept: "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });

      const json = await res.json();

      if (res.ok) {
        setProducts(prev =>
          prev.map(p => p.id === id ? { ...p, status: newStatus } : p)
        );
        return { success: true, data: { ...product, status: newStatus } };
      }

      return { success: false, message: json.message };
    } catch (err) {
      return { success: false, message: "Network error" };
    }
  };

  // ─── Delete product ──────────────────────────────────────────────────────────
  const deleteProduct = async (id) => {
    try {
      const deleteAuditId = localStorage.getItem("admin_id");

      const res = await fetch(`${PRODUCTS_ENDPOINT}/${id}`, {
        method: "DELETE",
        headers: { ...getHeaders(), Accept: "application/json" },
        body: JSON.stringify({ delete_audit_id: deleteAuditId }),
      });

      handleAuthError(res);

      if (res.ok) {
        setProducts(prev => prev.filter(p => String(p.id) !== String(id)));
      } else {
        const json = await res.json();
        alert(json?.message || "Delete failed");
      }
    } catch (err) {
      alert("Something went wrong");
    }
  };

  useEffect(() => {
    refreshProducts();
  }, [refreshProducts]);

  const value = useMemo(() => {
    // Build unique policy options from already-loaded products
    const policyMap = new Map();
    products.forEach(p => {
      if (p.return_policy && p.product_policy) {
        policyMap.set(String(p.return_policy), p.product_policy);
      }
    });
    const returnPolicies = Array.from(policyMap.entries()).map(([id, name]) => ({
      id,
      policy_name: name,
    }));

    return {
      products, loading, error,
      returnPolicies,
      refreshProducts, addProduct, updateProduct, deleteProduct, toggleProductStatus
    };
  }, [products, loading, error]);

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = () => {
  const ctx = useContext(ProductsContext);
  if (!ctx) throw new Error("useProducts must be used inside ProductsProvider");
  return ctx;
};
